import { useCallback, useEffect, useState } from 'react';
import { getTelegram, confirmDialog, alertDialog } from '../telegram';
import { acceptOrderDelivery, getOrders } from '../api';
import { WebBackButton } from '../components/WebNav';
import ClaimFormModal from '../components/ClaimFormModal';
import type { OrderShort } from '../types';

interface Props {
  onBack: () => void;
}

const STATUS_LABEL: Record<string, string> = {
  draft: 'Черновик',
  submitted: 'Принят',
  accepted: 'Подтверждён',
  shipped: 'Отгружен',
  disputed: 'На разборе',
  closed: 'Выполнен',
  cancelled: 'Отменён',
};

const STATUS_COLOR: Record<string, string> = {
  draft: 'var(--tg-hint)',
  submitted: '#ff9500',
  accepted: '#007aff',
  shipped: '#5856d6',
  disputed: '#ff9500',
  closed: '#34c759',
  cancelled: '#ff3b30',
};

const fmtPrice = (n: string) =>
  Number(n).toLocaleString('ru-RU', { style: 'currency', currency: 'RUB', minimumFractionDigits: 2 });

const fmtDate = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
};

// Срок приёмки: «28 мая, 14:05».
const fmtDeadline = (iso: string) =>
  new Date(iso).toLocaleString('ru-RU', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });

// Заголовок группы — операционная дата ("28 мая 2026"). Парсим вручную
// (YYYY-MM-DD), чтобы не зависеть от часового пояса браузера.
const fmtGroupDate = (s: string) => {
  const [y, m, dd] = s.split('-').map(Number);
  return new Date(y, m - 1, dd).toLocaleDateString('ru-RU', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
};

// Склонение числительных: 1 заказ, 2 заказа, 5 заказов.
const pluralRu = (n: number, [one, few, many]: [string, string, string]) => {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
};

// Группируем заказы по операционной дате. Список приходит отсортированным
// по created_at desc, поэтому операционные даты идут подряд — простая склейка.
function groupByOperationalDate(orders: OrderShort[]): { date: string; orders: OrderShort[] }[] {
  const groups: { date: string; orders: OrderShort[] }[] = [];
  for (const o of orders) {
    const last = groups[groups.length - 1];
    if (last && last.date === o.operational_date) last.orders.push(o);
    else groups.push({ date: o.operational_date, orders: [o] });
  }
  return groups;
}

const btnAccept: React.CSSProperties = {
  flex: 1, padding: '10px 8px', border: 'none', borderRadius: 10, background: '#34c759', color: '#fff',
  fontSize: 14, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer',
};
const btnClaim: React.CSSProperties = {
  ...btnAccept, background: 'rgba(255,149,0,0.16)', color: '#b35c00',
};

// Блок E: подпись об итоге приёмки у закрытого заказа.
function acceptanceSummary(order: OrderShort): string | null {
  if (order.status !== 'closed') return null;
  switch (order.acceptance_kind) {
    case 'client':
      return '✅ Принят вами';
    case 'auto':
      return '✅ Принят автоматически по истечении срока приёмки';
    case 'with_claim': {
      const refund = Number(order.claim_refund_total ?? 0);
      if (order.fulfillment_result === 'not_delivered') return `💳 Не доставлен: на депозит возвращено ${fmtPrice(String(refund))}`;
      return refund > 0
        ? `💳 По претензии на депозит возвращено ${fmtPrice(String(refund))}`
        : 'ℹ️ Претензия рассмотрена: возврат не предусмотрен';
    }
    default:
      return null;
  }
}

export default function OrderHistoryPage({ onBack }: Props) {
  const [orders, setOrders] = useState<OrderShort[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Блок E: заказ, по которому открыта форма «Есть расхождения», и занятая кнопка.
  const [claimFor, setClaimFor] = useState<OrderShort | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    const tg = getTelegram();
    if (!tg) return;
    tg.BackButton.show();
    tg.BackButton.onClick(onBack);
    tg.MainButton.hide();
    return () => { tg.BackButton.offClick(onBack); tg.BackButton.hide(); };
  }, [onBack]);

  const load = useCallback(() => {
    getOrders()
      .then(({ data }) => { setOrders(data); setError(null); })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const accept = async (order: OrderShort) => {
    const ok = await confirmDialog(
      `Принять заказ ${order.public_number}? Приёмка окончательна: претензии по нему в приложении больше не принимаются.`,
    );
    if (!ok) return;
    setBusyId(order.id);
    try {
      await acceptOrderDelivery(order.id);
      alertDialog(`Заказ ${order.public_number} принят. Спасибо!`);
      load();
    } catch (e) {
      alertDialog(`Не удалось принять заказ: ${(e as Error).message}`);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="container" style={{ padding: '16px 16px 32px' }}>
      <WebBackButton onClick={onBack} label="‹ В каталог" />
      <h3 style={{ margin: '0 0 16px', fontSize: 18 }}>История заказов</h3>

      {loading && <p className="hint">Загрузка…</p>}
      {error && <p className="hint" style={{ color: '#ff3b30' }}>{error}</p>}

      {!loading && !error && orders.length === 0 && (
        <p className="hint">Заказов пока нет</p>
      )}

      {!loading && !error && orders.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
          {groupByOperationalDate(orders).map(group => (
            <div key={group.date} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{
                fontWeight: 700, fontSize: 14,
                padding: '2px 2px 6px', borderBottom: '2px solid var(--tg-secondary-bg)',
              }}>
                {fmtGroupDate(group.date)}
                <span className="hint" style={{ fontWeight: 400, marginLeft: 8, fontSize: 13 }}>
                  · {group.orders.length} {pluralRu(group.orders.length, ['заказ', 'заказа', 'заказов'])}
                </span>
              </div>
              {group.orders.map(order => {
                // Блок B: подпись про депозит — резерв до отгрузки, списание после.
                const depositNote =
                  order.status === 'submitted' || order.status === 'accepted'
                    ? 'Сумма в резерве на депозите'
                    : order.status === 'shipped' || order.status === 'disputed' || order.status === 'closed'
                      ? 'Оплачен с депозита'
                      : null;
                const summary = acceptanceSummary(order);
                const busy = busyId === order.id;
                return (
                  <div key={order.id} className="card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                      <span style={{ fontWeight: 700, fontSize: 15 }}>{order.public_number}</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: STATUS_COLOR[order.status] ?? 'var(--tg-hint)' }}>
                        {STATUS_LABEL[order.status] ?? order.status}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span className="hint" style={{ fontSize: 13 }}>Доставка {fmtDate(order.delivery_date)}</span>
                      <span style={{ fontWeight: 700, color: 'var(--tg-button)' }}>{fmtPrice(order.total_with_vat)}</span>
                    </div>
                    {depositNote && (
                      <div className="hint" style={{ marginTop: 6, fontSize: 13 }}>💳 {depositNote}</div>
                    )}

                    {/* Блок E: приёмка отгруженного заказа — «Принять» или «Есть расхождения» */}
                    {order.status === 'shipped' && (
                      <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 10, background: 'var(--tg-bg)' }}>
                        <div style={{ fontSize: 13, lineHeight: 1.4, marginBottom: 8 }}>
                          📦 Ожидает приёмки
                          {order.acceptance_deadline ? ` до ${fmtDeadline(order.acceptance_deadline)}` : ''}.
                          Без действий будет принят автоматически.
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button onClick={() => accept(order)} disabled={busy} style={{ ...btnAccept, opacity: busy ? 0.6 : 1 }}>
                            ✅ Принять
                          </button>
                          <button onClick={() => setClaimFor(order)} disabled={busy} style={{ ...btnClaim, opacity: busy ? 0.6 : 1 }}>
                            ⚠️ Есть расхождения
                          </button>
                        </div>
                      </div>
                    )}
                    {order.status === 'disputed' && (
                      <div style={{ marginTop: 8, padding: '8px 10px', borderRadius: 8, background: 'rgba(255,149,0,0.12)', fontSize: 13, color: '#b35c00' }}>
                        ⚠️ Претензия на разборе у менеджера. Решение придёт в Telegram и на e-mail.
                      </div>
                    )}
                    {summary && (
                      <div className="hint" style={{ marginTop: 6, fontSize: 13 }}>{summary}</div>
                    )}

                    {/* Этап 23: причина отмены — показываем автору, почему заказ отменён */}
                    {order.status === 'cancelled' && order.cancellation_reason && (
                      <div style={{
                        marginTop: 8, padding: '8px 10px', borderRadius: 8,
                        background: 'rgba(255,59,48,0.10)', fontSize: 13, color: '#ff3b30',
                      }}>
                        Причина отмены: {order.cancellation_reason}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {claimFor && (
        <ClaimFormModal
          orderId={claimFor.id}
          orderNumber={claimFor.public_number}
          deadlineLabel={claimFor.acceptance_deadline ? fmtDeadline(claimFor.acceptance_deadline) : null}
          onClose={() => setClaimFor(null)}
          onFiled={() => { setClaimFor(null); load(); }}
        />
      )}
    </div>
  );
}
